// Paket / erişim modülü. Saf TypeScript: Deno ya da Node API'si kullanmaz,
// hem Edge Function'lar hem React ekranı göreli yolla içe aktarır.

export type Plan = 'free' | 'starter' | 'professional' | 'enterprise';
export type Tier = 'free' | 'trial' | 'paid' | 'grace' | 'read_only' | 'closed';
export type Feature =
  | 'label_printer'
  | 'driver_module'
  | 'report_history'
  | 'okc'
  | 'wash_packages'
  | 'remove_branding'
  | 'whatsapp_credit_purchase';

/** `null` = sınırsız */
export interface Limits {
  users: number | null;
  receiptsPerMonth: number | null;
  customers: number | null;
  whatsappPerDay: number | null;
}

export interface Access {
  tier: Tier;
  plan: Plan;
  limits: Limits;
  endsAt: string | null;
  daysLeft: number | null;
}

export const GRACE_DAYS = 7;
export const READ_ONLY_DAYS = 90;

export const PLAN_LIMITS: Record<Plan, Limits> = {
  free: { users: 1, receiptsPerMonth: 100, customers: 250, whatsappPerDay: 5 },
  starter: { users: 2, receiptsPerMonth: 1000, customers: null, whatsappPerDay: null },
  professional: { users: 4, receiptsPerMonth: 3000, customers: null, whatsappPerDay: null },
  enterprise: { users: null, receiptsPerMonth: null, customers: null, whatsappPerDay: null },
};

const DAY_MS = 24 * 60 * 60 * 1000;

function normalizePlan(plan: string | null | undefined): Plan {
  if (plan === 'free' || plan === 'starter' || plan === 'professional' || plan === 'enterprise') return plan;
  return 'starter';
}

function parseEnd(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function build(tier: Tier, plan: Plan, end: Date | null, now: Date, withDaysLeft: boolean): Access {
  return {
    tier,
    plan,
    limits: { ...PLAN_LIMITS[plan] },
    endsAt: end ? end.toISOString() : null,
    daysLeft: withDaysLeft && end ? Math.ceil((end.getTime() - now.getTime()) / DAY_MS) : null,
  };
}

export function resolveAccess(
  t: { plan?: string | null; status?: string | null; subscription_end_date?: string | null },
  now: Date,
): Access {
  const plan = normalizePlan(t.plan);
  if (t.plan === 'free') return build('free', 'free', null, now, false);

  const end = parseEnd(t.subscription_end_date);

  if (t.status === 'trial') {
    if (end && end.getTime() > now.getTime()) return build('trial', 'starter', end, now, true);
    return build('free', 'free', end, now, false);
  }

  if (!end || end.getTime() > now.getTime()) return build('paid', plan, end, now, true);

  const sinceEnd = now.getTime() - end.getTime();
  if (sinceEnd <= GRACE_DAYS * DAY_MS) return build('grace', plan, end, now, true);
  if (sinceEnd <= READ_ONLY_DAYS * DAY_MS) return build('read_only', plan, end, now, false);
  return build('closed', plan, end, now, false);
}

export function hasFeature(a: Access, _f: Feature): boolean {
  return a.tier !== 'free';
}

export function canWrite(a: Access): boolean {
  return a.tier !== 'read_only' && a.tier !== 'closed';
}
